import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Users, MapPin, Play, Star } from 'lucide-react';
import { TrainingDrill } from '../Model';
import { getDifficultyColor } from './drillColors';

interface DrillCardProps {
  drill: TrainingDrill;
  onClick: () => void;
  isAdmin?: boolean;
  isFavourite?: boolean;
  onToggleFavourite?: (drillId: number) => void;
}

export const DrillCard: React.FC<DrillCardProps> = ({ drill, onClick, isAdmin = false, isFavourite = false, onToggleFavourite }) => {
  const navigate = useNavigate();
  const difficultyColors = getDifficultyColor(drill.difficulty);

  // Limit visible skills to 2 for cleaner cards
  const visibleSkills = drill.skills.slice(0, 2);
  const hasMoreSkills = drill.skills.length > 2;

  // Show only first age group if multiple
  const primaryAgeGroup = drill.ageGroups?.[0];
  const hasMoreAgeGroups = (drill.ageGroups?.length ?? 0) > 1;

  return (
    <div
      role="button"
      tabIndex={0}
      className={`drill-card${drill.isHidden ? ' drill-card-hidden' : ''}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`View details for ${drill.name}`}
    >
      {/* 1. Title + Hidden Badge + Favourite Star */}
      <div className="drill-card-header">
        <h3 className="drill-card-title" title={drill.name}>{drill.name}</h3>
        {drill.isHidden && (
          <span className="badge badge-sm drill-badge-hidden">Hidden</span>
        )}
        {onToggleFavourite && (
          <button
            className={`drill-card-favourite ${isFavourite ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavourite(drill.id);
            }}
            aria-label={isFavourite ? `Remove ${drill.name} from favourites` : `Add ${drill.name} to favourites`}
          >
            <Star size={16} fill={isFavourite ? 'currentColor' : 'none'} />
          </button>
        )}
      </div>

      {/* 2. Key Facts Row (promoted to top) */}
      <div className="drill-card-facts">
        <span className="drill-fact">
          <Clock size={12} />
          {drill.duration}m
        </span>
        <span className="drill-fact">
          <Users size={12} />
          {drill.playersRequired}
        </span>
        <span className="drill-fact">
          <MapPin size={12} />
          {drill.spaceRequired.width}×{drill.spaceRequired.height}
        </span>
      </div>

      {/* 3. Primary Badges (max 2 total: difficulty + age) */}
      <div className="drill-card-badges">
        <span
          className="drill-badge drill-badge-difficulty"
          style={{
            backgroundColor: difficultyColors.bg,
            color: difficultyColors.text,
            borderColor: difficultyColors.border,
          }}
        >
          {drill.difficulty}
        </span>
        {primaryAgeGroup && (
          <span className="drill-badge drill-badge-age">
            {primaryAgeGroup}
            {hasMoreAgeGroups && ` +${(drill.ageGroups?.length ?? 1) - 1}`}
          </span>
        )}
      </div>

      {/* 4. Description (max 2 lines, truncated) */}
      <p className="drill-card-description">{drill.description}</p>

      {/* 5. Skills (revealed on hover) */}
      <div className="drill-card-skills">
        {visibleSkills.map((skill) => (
          <span key={skill} className="drill-skill-tag">
            {skill}
          </span>
        ))}
        {hasMoreSkills && (
          <span className="drill-skill-tag drill-skill-more">+{drill.skills.length - 2}</span>
        )}
      </div>

      {/* 6. Actions */}
      <div className="drill-card-actions">
        {drill.youtubeUrl && (
          <a
            href={drill.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-primary drill-card-video-btn"
            onClick={(e) => e.stopPropagation()}
          >
            <Play size={14} /> Watch Video
          </a>
        )}
        {isAdmin && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/training-drills/edit/${drill.id}`);
            }}
            aria-label={`Edit drill ${drill.name}`}
            className="btn btn-sm btn-outline-secondary drill-card-edit-btn"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
};
